import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

interface RoutePrefetchOptions {
  /**
   * Routes to prefetch
   * Format: { path: string, component: React.LazyExoticComponent }
   */
  routes: Array<{
    path: string;
    component: React.LazyExoticComponent<React.ComponentType<any>>;
  }>;
  /**
   * Delay before prefetching (ms)
   */
  delay?: number;
  /**
   * Prefetch on page load (for critical routes)
   */
  prefetchOnLoad?: boolean;
}

/**
 * Hook to prefetch route chunks on link hover/focus
 * Improves perceived performance by loading route code before navigation
 */
export function useRoutePrefetch({ routes, delay = 100, prefetchOnLoad = false }: RoutePrefetchOptions) {
  const [location] = useLocation();
  const prefetchedRef = useRef<Set<string>>(new Set());
  const timeoutRef = useRef<Map<HTMLElement, NodeJS.Timeout>>(new Map());

  // Prefetch a route chunk
  const prefetchRoute = (path: string, component: React.LazyExoticComponent<React.ComponentType<any>>) => {
    // Skip if already prefetched or current route
    if (prefetchedRef.current.has(path) || location === path) {
      return;
    }

    // Skip dashboard routes on public pages (intelligent prefetch)
    if (path.startsWith('/dashboard') || path.startsWith('/test-lexical')) {
      return;
    }

    try {
      // Preload the lazy component
      if (component && typeof component === 'object' && '_payload' in component) {
        const lazyComponent = component;
        if (lazyComponent._payload?._status === 0) {
          // Component not loaded yet, trigger preload
          lazyComponent._payload._result
            .then(() => {
              prefetchedRef.current.add(path);
            })
            .catch(() => {
              // Silently fail if prefetch fails
            });
        }
      }
    } catch (error) {
      // Silently fail if prefetch fails
    }

    prefetchedRef.current.add(path);
  };

  // Prefetch on page load for critical routes
  useEffect(() => {
    if (prefetchOnLoad) {
      routes.forEach(({ path, component }) => {
        if (location !== path) {
          prefetchRoute(path, component);
        }
      });
    }
  }, [prefetchOnLoad]); // Only run once on mount

  // Handle link hover/focus
  useEffect(() => {
    if (routes.length === 0) return;

    const handleLinkInteraction = (
      e: Event,
      path: string,
      component: React.LazyExoticComponent<React.ComponentType<any>>
    ) => {
      const target = e.currentTarget as HTMLElement;

      // Clear existing timeout
      const existingTimeout = timeoutRef.current.get(target);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout for prefetch
      const timeout = setTimeout(() => {
        prefetchRoute(path, component);
        timeoutRef.current.delete(target);
      }, delay);

      timeoutRef.current.set(target, timeout);
    };

    // Find all links matching the routes
    routes.forEach(({ path, component }) => {
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const links = document.querySelectorAll(`a[href="${normalizedPath}"], a[href="${path}"]`);

      links.forEach((link) => {
        link.addEventListener('mouseenter', (e) => handleLinkInteraction(e, path, component));
        link.addEventListener('touchstart', (e) => handleLinkInteraction(e, path, component), { passive: true });
        link.addEventListener('focus', (e) => handleLinkInteraction(e, path, component));
      });
    });

    // Cleanup
    return () => {
      timeoutRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutRef.current.clear();
    };
  }, [routes, delay, location]);
}


















































